import { Injectable } from "@angular/core";
import { MainState } from "@app/store/state";
import { AuthService } from "@core/services/auth.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { CompleteTutorial, ResetTutorial } from "@features/account/store/auth.actions";
import { selectCurrentUserProfile } from "@features/account/store/auth.selectors";
import { Store } from "@ngrx/store";
import { CookieService } from "ngx-cookie";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";

export interface TutorialStep {
  id: string;
  selector: string;
  content: string;
  placement?: string; // "top" | "bottom" | "left" | "right"
  arrowPositionTop?: string;
  arrowPositionLeft?: string;
}

export interface Tutorial {
  id: string;
  steps: TutorialStep[];
  unskippable?: boolean; // defaults to false if not specified
  enableNotNow?: boolean; // defaults to false if not specified
}

@Injectable({
  providedIn: "root"
})
export class TutorialService {
  private readonly _activeTutorial = new BehaviorSubject<Tutorial | null>(null);
  private readonly _currentStepIndex = new BehaviorSubject<number>(0);
  private readonly _cookiePrefix = "astrobin_tutorial_";
  private readonly _suspensionCookieName = "astrobin_tutorials_suspended";
  private _initializedFromStore = false;
  private _cachedUserProfile: any = null;
  private _onTutorialComplete: (() => void) | null = null;

  constructor(
    private readonly cookieService: CookieService,
    private readonly authService: AuthService,
    private readonly utilsService: UtilsService,
    private readonly store: Store<MainState>
  ) {
    // Initialize data from store
    this._initializeFromStore();

    // Cache user profile asynchronously to avoid blocking UI
    this.authService.isAuthenticated$().subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.store.select(selectCurrentUserProfile).subscribe(profile => {
          this._cachedUserProfile = profile;
        });
      }
    });
  }

  public get activeTutorial$(): Observable<Tutorial | null> {
    return this._activeTutorial.asObservable();
  }

  public get currentStepIndex$(): Observable<number> {
    return this._currentStepIndex.asObservable();
  }

  /**
   * Starts a tutorial with the given ID and steps
   */
  public startTutorial(tutorial: Tutorial, onComplete?: () => void): void {
    // If we haven't initialized from store yet, wait a moment before checking completion status
    if (!this._initializedFromStore) {
      this.utilsService.delay(500).subscribe(() => {
        this.startTutorial(tutorial, onComplete);
      });
      return;
    }

    // Check if tutorials are suspended
    if (this.areTutorialsSuspended()) {
      return;
    }

    if (this.hasTutorialBeenCompleted(tutorial.id)) {
      return;
    }

    this._onTutorialComplete = onComplete || null;
    this._activeTutorial.next(tutorial);
    this._currentStepIndex.next(0);
  }

  /**
   * Ends the current tutorial
   */
  public endTutorial(completed = true): void {
    const tutorial = this._activeTutorial.getValue();

    if (tutorial && completed) {
      this._markTutorialAsCompleted(tutorial.id);
    }

    this._activeTutorial.next(null);
    this._currentStepIndex.next(0);

    // Call completion callback if provided
    if (completed && this._onTutorialComplete) {
      const callback = this._onTutorialComplete;
      this._onTutorialComplete = null;
      callback();
    }
  }

  /**
   * Moves to the next step in the tutorial
   */
  public nextStep(): void {
    const currentIndex = this._currentStepIndex.getValue();
    const tutorial = this._activeTutorial.getValue();

    if (!tutorial) {
      return;
    }

    if (currentIndex < tutorial.steps.length - 1) {
      this._currentStepIndex.next(currentIndex + 1);
    } else {
      this.endTutorial(true);
    }
  }

  /**
   * Checks if a tutorial has been completed, either in cookies or on the server (via store)
   */
  public hasTutorialBeenCompleted(tutorialId: string): boolean {
    // First check in cookies for backward compatibility
    const completedInCookie = !!this.cookieService.get(`${this._cookiePrefix}${tutorialId}`);
    if (completedInCookie) {
      return true;
    }

    // For authenticated users, check if we have cached the user profile
    // This avoids synchronous subscriptions that block the UI
    if (this._cachedUserProfile) {
      return !!(this._cachedUserProfile.completedTutorials && this._cachedUserProfile.completedTutorials[tutorialId]);
    }

    // If no cached profile, return false to avoid blocking
    // The profile will be loaded asynchronously in the constructor
    return false;
  }

  /**
   * Resets a tutorial's completion status from cookies and store/server
   */
  public resetTutorial(tutorialId: string): Observable<boolean> {
    // Remove from cookies
    this.cookieService.remove(`${this._cookiePrefix}${tutorialId}`);

    // Remove from store/server if authenticated
    return this.authService.isAuthenticated$().pipe(
      take(1),
      map(isAuthenticated => {
        if (isAuthenticated) {
          this.store.dispatch(new ResetTutorial({ tutorialId }));
        }
        return true;
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Resets all tutorial completion statuses
   */
  public resetAllTutorials(): Observable<boolean> {
    // Get all cookie keys
    const allCookies = this.cookieService.getAll();
    const tutorialCookies = Object.keys(allCookies).filter(key => key.startsWith(this._cookiePrefix));

    // Remove all tutorial cookies
    for (const cookieName of tutorialCookies) {
      this.cookieService.remove(cookieName);
    }

    // Remove all server-stored tutorials through store if authenticated
    return this.authService.isAuthenticated$().pipe(
      take(1),
      map(isAuthenticated => {
        if (isAuthenticated) {
          // Get the completed tutorials from the current user profile
          this.store
            .select(selectCurrentUserProfile)
            .pipe(take(1))
            .subscribe(userProfile => {
              if (userProfile?.completedTutorials) {
                const tutorialIds = Object.keys(userProfile.completedTutorials);
                for (const tutorialId of tutorialIds) {
                  this.store.dispatch(new ResetTutorial({ tutorialId }));
                }
              }
            });
        }
        return true;
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Initialize from the store which already has the user profile with completed_tutorials
   */
  private _initializeFromStore(): void {
    this.store
      .select(selectCurrentUserProfile)
      .pipe(take(1))
      .subscribe(() => {
        this._initializedFromStore = true;
      });
  }

  /**
   * Marks a tutorial as completed both in cookies and store
   */
  private _markTutorialAsCompleted(tutorialId: string): void {
    // Set cookie that expires in 1 year (for backward compatibility)
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);

    const options = {
      expires: expiryDate,
      path: "/"
    };

    this.cookieService.put(`${this._cookiePrefix}${tutorialId}`, "completed", options);

    // Also save to store/server if authenticated
    this.authService
      .isAuthenticated$()
      .pipe(take(1))
      .subscribe(isAuthenticated => {
        if (isAuthenticated) {
          this.store.dispatch(new CompleteTutorial({ tutorialId }));
        }
      });
  }

  /**
   * Suspends all tutorials for 3 days by setting a cookie
   */
  public suspendAllTutorials(): void {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 3); // Expire in 3 days

    const options = {
      expires: expiryDate,
      path: "/"
    };

    this.cookieService.put(this._suspensionCookieName, "true", options);
  }

  /**
   * Checks if tutorials are currently suspended
   */
  public areTutorialsSuspended(): boolean {
    return !!this.cookieService.get(this._suspensionCookieName);
  }
}
